import { useState } from "react";
import { AlertTriangle, RotateCcw, Trash2 } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LOST_REASON_MIN, OPPORTUNITY_NAME_MAX, ROLES, type Opportunity } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { OwnerBadge } from "@/components/ui/OwnerBadge";
import { StageChip } from "@/components/ui/StageChip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DetailAction, DetailModal } from "@/components/ui/DetailModal";
import { ErrorState, LoadingState } from "@/components/ui/states";
import {
  InlineCurrency,
  InlineDate,
  InlineSelect,
  InlineText,
  InlineTextarea,
  type InlineOption,
} from "@/components/ui/inline";
import { ApiClientError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useSession } from "@/features/auth/useSession";
import { AuditTimeline } from "@/features/audit/AuditTimeline";
import { useIsDisconnected } from "@/app/ConnectionState";
import { useMoveFlow } from "@/features/board/useMoveFlow";
import { useOpenDetail } from "@/app/useOpenDetail";
import { coldBackgroundPath, type DetailLocationState } from "@/app/detail-navigation";
import { useCanonicalSlug } from "@/app/useCanonicalSlug";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import {
  useDeleteOpportunity,
  useOpportunityBySlug,
  useOpportunityHistory,
  useOwners,
  useReassignOwner,
  useStages,
  useUpdateOpportunity,
} from "./hooks";

/**
 * One opportunity, as a compact hero over whatever the reader was already looking at.
 *
 * Two things this surface is built around:
 *
 * **Everything editable is edited by clicking it.** No Edit dialog, no Move dialog — a value
 * becomes an input, Enter or blur commits that one field with the version it was read at, and
 * Escape writes nothing. The hover highlight is the affordance; its absence on a value means
 * this viewer cannot change it. See `components/ui/inline`.
 *
 * **The collapsed set is the meeting set.** Stage (with how long it has sat there), expected
 * close (with the quarter it derives), expected revenue, sales manager — and nothing else.
 * Housekeeping lives behind "Show more info", collapsed on every open: the four facts above
 * are designed to be sufficient, and a persisted expansion would let one reader's habit hide
 * that design from the next.
 *
 * Design: specs/opportunity-detail-hero.md.
 */
export function OpportunityModal(): React.JSX.Element {
  const t = useT();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const openDetail = useOpenDetail();
  const session = useSession();

  const query = useOpportunityBySlug(slug);
  // A link taken before a rename opens the right record; this makes the address bar
  // catch up, so the stale slug is not copied onward from here.
  useCanonicalSlug(slug, query.data?.slug, "/opportunity");
  const [showAllHistory, setShowAllHistory] = useState(false);
  const history = useOpportunityHistory(query.data?.id, showAllHistory ? 200 : undefined);
  // The record's name in the tab while the modal is open, restored to the surface's when it
  // closes. Undefined until it loads, so the tab never flashes a placeholder.
  useDocumentTitle(query.data?.name);

  const [deleteOpen, setDeleteOpen] = useState(false);
  /** A reassignment awaiting its confirmation. Nothing is sent until it is confirmed. */
  const [reassignTo, setReassignTo] = useState<InlineOption>();

  const id = query.data?.id ?? "";
  const update = useUpdateOpportunity(id);
  const reassign = useReassignOwner(id);
  const remove = useDeleteOpportunity();

  // The board's move flow, reused verbatim: the same confirmations, the same version guard,
  // the same audit trail whether a stage change started here or as a drag.
  const stages = useStages();
  const owners = useOwners();
  // No `?? new Date().getFullYear()`: the year is a derived value the server owns, and
  // the flow is only reachable once the opportunity has loaded and carries its own.
  const flow = useMoveFlow({ stages: stages.data, boardYear: query.data?.year });
  // Mutations pause while live updates are not arriving, here as on the board: editing a
  // record whose changes you cannot see is how two people overwrite each other.
  const disconnected = useIsDisconnected();

  if (query.isPending) {
    return (
      // The same minimum the loaded body settles into, so the skeleton is not a different size
      // from the thing it stands in for. Six rows of skeleton were 100px taller than the record
      // they preceded.
      <DetailModal title={t("opportunity.loading")}>
        <div className="min-h-[19rem]">
          <LoadingState rows={4} label={t("opportunity.loading")} />
        </div>
      </DetailModal>
    );
  }

  if (query.error || !query.data) {
    return (
      <DetailModal title={t("opportunity.notFound")}>
        <ErrorState
          message={query.error instanceof ApiClientError ? query.error.message : t("opportunity.loadFailed")}
          requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
          onRetry={() => void query.refetch()}
          action={{ label: t("opportunity.backToBoard"), onClick: () => void navigate("/", { replace: true }) }}
        />
      </DetailModal>
    );
  }

  const opportunity = query.data;
  const user = session.data;
  // Ownership is the write control. Mirrored here for presentation only — the API applies
  // the same rule and refuses regardless of what was rendered.
  const isHeadOfSales = user?.role === ROLES.headOfSales;
  const canEdit = user ? isHeadOfSales || user.id === opportunity.ownerId : false;
  /** Reassignment is Head-of-Sales-only, which is a narrower rule than editing. */
  const canReassign = Boolean(isHeadOfSales);

  const activeStages = (stages.data ?? []).filter((stage) => stage.active);
  const firstOpenStage = activeStages.find((stage) => stage.closedType === "open");

  /** What the move flow needs. Every field is already on the record we hold. */
  const movable = {
    id: opportunity.id,
    name: opportunity.name,
    clientName: opportunity.clientName,
    stageId: opportunity.stageId,
    quarter: opportunity.quarter,
    year: opportunity.year,
    status: opportunity.status,
    version: opportunity.version,
    ownerId: opportunity.ownerId,
    ownerName: opportunity.ownerName,
    actualCloseDate: opportunity.actualCloseDate,
  };

  /**
   * One field, one request.
   *
   * `mutateAsync` resolves after the refetch, so the inline field leaves edit mode on the
   * server's committed value. It is not caught here: the inline field needs the rejection to
   * tell a validation failure (stay open, show the message) from a refused stale write
   * (return to display, the reporter has toasted it and re-read the record).
   */
  const patch = (input: Parameters<typeof update.mutateAsync>[0]): Promise<Opportunity> =>
    update.mutateAsync(input);

  // Computed once, ahead of the modal below: its truthiness both selects what renders in
  // the actions slot and decides how much clearance the identity row needs from it — one
  // condition, not two copies that could drift out of step with each other.
  const actionsNode = canEdit ? (
    <>
      {/* Reopen exists only where there is something to reopen. It targets the
          board's first open stage and confirms through the same ReopenDialog a drag
          does; the stage select below reaches any other stage. */}
      {opportunity.status !== "open" && firstOpenStage && (
        <DetailAction
          label={t("opportunity.reopen")}
          disabled={disconnected}
          onClick={() => flow.begin(movable, { stageId: firstOpenStage.id, quarter: opportunity.quarter })}
        >
          <RotateCcw className="h-4 w-4" aria-hidden />
        </DetailAction>
      )}
      <DetailAction label={t("opportunity.delete")} destructive disabled={disconnected} onClick={() => setDeleteOpen(true)}>
        <Trash2 className="h-4 w-4" aria-hidden />
      </DetailAction>
    </>
  ) : undefined;

  return (
    <>
      <DetailModal
        title={opportunity.name}
        actions={actionsNode}
        header={
          <div className="space-y-3">
            {/* The only row that sits beside the action icons and the close ✕, so the only
                one that needs to clear them — `pr-40` matches DetailModal's old blanket
                padding, just aimed at the one row that was ever actually under them. The
                facts grid below is a full row lower and was never in their way. */}
            <div className={cn("min-w-0", actionsNode && "pr-40")}>
              {/* Through `openDetail`, never a bare Link: a plain push arrives with no
                  background, so the modal would render over the *default* board rather
                  than the filtered one behind it, and closing would replace to `/`
                  instead of stepping back here. */}
              <button
                type="button"
                onClick={() => openDetail(`/client/${opportunity.clientSlug}`)}
                className="text-muted-foreground hover:text-foreground text-sm hover:underline"
              >
                {opportunity.clientName}
              </button>

              <h2 className="min-w-0">
                <InlineText
                  label={t("opportunity.name")}
                  value={opportunity.name}
                  editable={canEdit}
                  disabled={disconnected}
                  size="lg"
                  maxLength={OPPORTUNITY_NAME_MAX}
                  onCommit={(name) => patch({ name, version: opportunity.version })}
                />
              </h2>
            </div>

            {/* The collapsed set: the four facts a meeting needs — five once a record has an
                actual close date.

                Sized against the **dialog**, not the window (`@` variants, container declared
                on `.detail-header`). With viewport breakpoints a 1440px window took the wide
                branch and packed five columns into the modal's 42rem, giving each fact ~130px:
                "Expected revenue" wrapped to two lines, its value dropped to a third and ran
                into the manager beside it, and the row lost the single baseline that makes
                four facts read as four facts. */}
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 @lg:grid-cols-3 @xl:grid-cols-4 @4xl:grid-cols-5">
              <HeroFact label={t("opportunity.stage")}>
                <InlineSelect
                  label={t("opportunity.stage")}
                  value={opportunity.stageId}
                  options={activeStages.map((stage) => ({ value: stage.id, label: stage.name }))}
                  editable={canEdit}
                  disabled={disconnected}
                  // The flow owns this commit — a closed target opens a dialog before
                  // anything is sent — so the spinner follows the flow, not a promise here.
                  pending={flow.committing}
                  onCommit={async (stageId) => {
                    flow.begin(movable, { stageId, quarter: opportunity.quarter });
                  }}
                  /* The configured stage colour, through the *same* `StageChip` the list
                     renders (2026-08-26). One rendering of "a stage wearing its colour", so
                     the detail and the list cannot drift apart about a hue an administrator
                     set — and the chip's text is still the stage name, which is what keeps
                     the outcome readable without the colour. */
                  display={
                    <StageChip
                      name={opportunity.stageName}
                      closedType={opportunity.status}
                      color={opportunity.stageColor}
                    />
                  }
                  beneath={<StageAge opportunity={opportunity} />}
                />
              </HeroFact>

              <HeroFact label={t("opportunity.expectedClose")}>
                <InlineDate
                  label={t("opportunity.expectedCloseDate")}
                  value={opportunity.expectedCloseDate}
                  quarter={opportunity.quarter}
                  year={opportunity.year}
                  editable={canEdit}
                  disabled={disconnected}
                  onCommit={(expectedCloseDate) => patch({ expectedCloseDate, version: opportunity.version })}
                />
              </HeroFact>

              <HeroFact label={t("opportunity.expectedRevenue")}>
                <InlineCurrency
                  label={t("opportunity.expectedRevenue")}
                  value={opportunity.nip}
                  editable={canEdit}
                  disabled={disconnected}
                  onCommit={(nip) => patch({ nip, version: opportunity.version })}
                />
              </HeroFact>

              {/* Promoted out of the retired "Show more" area (2026-08-26): on a closed record
                  this is one of the four facts that matter, and on an open one it does not
                  exist. Read-only — the closure sets it. */}
              {opportunity.actualCloseDate && (
                <HeroFact label={t("opportunity.actualClose")}>
                  <span className="text-sm">{formatDate(opportunity.actualCloseDate)}</span>
                </HeroFact>
              )}

              <HeroFact label={t("opportunity.salesManager")}>
                <InlineSelect
                  label={t("opportunity.salesManager")}
                  value={opportunity.ownerId}
                  options={(owners.data ?? []).map((owner) => ({ value: owner.id, label: owner.name }))}
                  editable={canReassign}
                  disabled={disconnected}
                  pending={reassign.isPending}
                  /* The badge before the name, exactly as the board card and the list draw it
                     (2026-08-26) — the one place the owner's colour survives in this detail,
                     since the header's own tint was retired for being read as a status. */
                  display={
                    <span className="inline-flex items-center gap-1.5">
                      <OwnerBadge name={opportunity.ownerName} color={opportunity.ownerColor} />
                      {opportunity.ownerName}
                    </span>
                  }
                  // Reassignment is confirmed before anything is sent — the book requires
                  // it, and the consequence it has to state is not obvious.
                  onCommit={async (ownerId) => {
                    const chosen = owners.data?.find((owner) => owner.id === ownerId);
                    if (chosen) setReassignTo({ value: chosen.id, label: chosen.name });
                  }}
                  // The name keeps one line: a two-line "Petr / Dvořák" beside a two-line
                  // date made the hero's four columns read as eight.
                  className="whitespace-nowrap"
                  // The way to the profile is a *worded* link on the second row, where every
                  // other column's companion fact sits — replacing a bare person glyph that
                  // named its destination only in a tooltip.
                  beneath={
                    <button
                      type="button"
                      onClick={() => openDetail(`/user/${opportunity.ownerSlug}`)}
                      className="text-muted-foreground hover:text-foreground focus-visible:ring-ring mt-0.5 rounded text-xs underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none"
                    >
                      {t("opportunity.openManager")}
                    </button>
                  }
                />
              </HeroFact>
            </dl>

            {/*
              Notes are here, always, and editable by clicking them (2026-08-26).
              They used to sit behind a "Show more info" toggle beside an "+ Add note" button —
              three interactions to write a sentence, on the one field a reader is most likely
              to want to add. The toggle is gone entirely: with last-modified removed and the
              actual close date promoted into the facts row above, it had nothing left to hide.
            */}
            <div className="border-border/60 space-y-3 border-t pt-3">
              <div>
                <dt className="text-muted-foreground mb-1 text-xs">{t("opportunity.notes")}</dt>
                <InlineTextarea
                  label={t("opportunity.notes")}
                  value={opportunity.notes}
                  editable={canEdit}
                  disabled={disconnected}
                  placeholder={t("opportunity.notePlaceholder")}
                  onCommit={async (notes) => {
                    // Nothing to send when an empty field is left empty: no note before, none
                    // after, and an audit row saying so would be noise.
                    if (notes === null && opportunity.notes === null) return;
                    await patch({ notes, version: opportunity.version });
                  }}
                />
              </div>

              {/*
                Why it was lost — only on a lost record, and never empty on one.
                Closing as lost demands it in the closure dialog, and anything closed before
                that rule existed is asked for at sign-in; this is where it is *read* and
                corrected. Same inline field as the note, so the two behave alike.
              */}
              {opportunity.status === "lost" && (
                <div>
                  <dt className="text-destructive mb-1 text-xs font-medium">{t("opportunity.lostReason")}</dt>
                  <InlineTextarea
                    label={t("opportunity.lostReason")}
                    value={opportunity.lostReason}
                    editable={canEdit}
                    disabled={disconnected}
                    placeholder={t("opportunity.lostReasonPlaceholder")}
                    // Refused before anything is sent: a lost opportunity with no reason is the
                    // state this field exists to prevent, so the field keeps what was there and
                    // says what it needs rather than clearing and reporting afterwards.
                    validate={(next) =>
                      (next ?? "").trim().length < LOST_REASON_MIN
                        ? t("opportunity.lostReasonTooShort", LOST_REASON_MIN)
                        : undefined
                    }
                    onCommit={async (lostReason) => {
                      await patch({ lostReason: lostReason ?? "", version: opportunity.version });
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        }
      >
        {/*
          The history's own box is reserved, because it loads on a **second** query and used to
          resize the dialog underneath the reader. Measured: the modal settled at 415px on the
          detail's skeleton, grew to 427 as the hero arrived over the history's skeleton, then
          snapped to 315 when one history row replaced three skeleton rows — three heights in
          30ms, which is the "layout shift" nobody could pin down.
          `min-h` holds the space its skeleton occupies, so the swap fills a box that was
          already there. Records with a long history grow past it, which is fine: that growth
          happens once, below the fold, and nothing above it moves.
        */}
        <section className="min-h-[9.5rem]">
          <h3 className="mb-3 text-sm font-medium">{t("opportunity.history")}</h3>
          {history.isPending && <LoadingState rows={3} label={t("opportunity.historyLoading")} />}
          {history.error && (
            <ErrorState
              message={
                history.error instanceof ApiClientError ? history.error.message : t("opportunity.historyFailed")
              }
              requestId={history.error instanceof ApiClientError ? history.error.requestId : "unknown"}
              onRetry={() => void history.refetch()}
            />
          )}
          {history.data && (
            <AuditTimeline
              events={history.data.items}
              total={history.data.total}
              showingAll={showAllHistory}
              onShowAll={() => setShowAllHistory(true)}
            />
          )}
        </section>
      </DetailModal>

      {/* Stacked above the detail: the lifecycle dialogs are the product rules — a closure
          without a date is not a closure — and they already carry the version guard. */}
      {flow.dialogs}

      {reassignTo && (
        <ConfirmDialog
          open
          onOpenChange={(next) => !next && setReassignTo(undefined)}
          title={t("opportunity.reassignTitle", reassignTo.label)}
          consequence={t("opportunity.reassignConsequence", reassignTo.label)}
          confirmLabel={t("opportunity.reassignConfirm")}
          pending={reassign.isPending}
          onConfirm={async () => {
            try {
              await reassign.mutateAsync({ ownerId: reassignTo.value, version: opportunity.version });
            } catch {
              // Reported by the shared reporter, with the diagnostic identifier. Swallowed
              // so a refusal is not an unhandled rejection in the browser.
            } finally {
              setReassignTo(undefined);
            }
          }}
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t("opportunity.deleteTitle", opportunity.name)}
        consequence={t("opportunity.deleteConsequence")}
        confirmLabel={t("opportunity.delete")}
        destructive
        pending={remove.isPending}
        onConfirm={async () => {
          await remove.mutateAsync({ id: opportunity.id, version: opportunity.version });
          setDeleteOpen(false);
          // Both layers close, and we land on the surface this modal was opened over —
          // the *filtered* board or the registry, not a default. Stepping back is wrong
          // here specifically: the record no longer exists, so history would reopen a
          // not-found state.
          const background = (location.state as DetailLocationState)?.background?.location;
          void navigate(background ?? coldBackgroundPath(location.pathname), { replace: true });
        }}
      />
    </>
  );
}


/** A labelled fact in the hero. Like `DetailFact`, but it never truncates an open input. */
function HeroFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

/**
 * How long it has sat in this stage, under the stage itself.
 *
 * Both numbers come from `opportunity_derived` in the detail response — the same view the
 * board card reads. Never a date difference computed here: "whole days elapsed" is defined
 * once, in SQL, in `APP_TIMEZONE`.
 */
function StageAge({ opportunity }: { opportunity: Opportunity }) {
  const t = useT();

  /**
   * How long it has sat here, in a sentence.
   *
   * No brackets (2026-08-26): a parenthesis around every second-row value made the hero read as
   * a page of asides. And stalled says so in words — "Stalled for 68 days" is what a reader
   * would say out loud, where "(68 days) Stalled" was a number and a label to assemble.
   *
   * The warning shape leads, as it must: status is never carried by hue alone, and the number
   * is in the sentence rather than only in a tooltip, so nothing here is hover-exclusive.
   */
  if (opportunity.stalled) {
    return (
      <p className="text-destructive mt-0.5 flex items-center gap-1 text-xs font-medium">
        <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
        {t("card.stalledFor", opportunity.stageAgeDays)}
      </p>
    );
  }

  return <p className="text-muted-foreground mt-0.5 text-xs">{t("count.daysInStage", opportunity.stageAgeDays)}</p>;
}

